import { createApiService } from "@/core/api/apiService";
import type {
  AvailabilityDto,
  AvailabilityOverrideDto,
  CreateAvailabilityDto,
  CreateAvailabilityOverrideDto,
  TimeSlot,
  UpdateAvailabilityDto,
} from "../types/availabilityTypes";

function createAvailabilityApiService() {
  const api = createApiService("availability");

  return {
    getAll: async (params?: {
      userId?: string;
      businessId?: string;
      locationId?: string;
    }): Promise<AvailabilityDto[]> => {
      const queryParams = new URLSearchParams();
      if (params?.userId) queryParams.append("userId", params.userId);
      if (params?.businessId)
        queryParams.append("businessId", params.businessId);
      if (params?.locationId)
        queryParams.append("locationId", params.locationId);

      const queryString = queryParams.toString();
      return api.get<AvailabilityDto[]>(queryString ? `/?${queryString}` : "/");
    },

    create: async (data: CreateAvailabilityDto): Promise<AvailabilityDto> => {
      return api.post<AvailabilityDto>("", data);
    },

    update: async (
      availabilityId: string,
      data: UpdateAvailabilityDto
    ): Promise<AvailabilityDto> => {
      return api.put<AvailabilityDto>(`/${availabilityId}`, data);
    },

    delete: async (availabilityId: string): Promise<{ message: string }> => {
      return api.delete<{ message: string }>(`/${availabilityId}`);
    },

    getAllOverrides: async (params?: {
      userId: string;
      businessId: string;
      locationId: string;
      startDate: string;
      endDate: string;
      isActive?: boolean;
    }): Promise<AvailabilityOverrideDto[]> => {
      const queryParams = new URLSearchParams();
      if (params?.userId) queryParams.append("userId", params.userId);
      if (params?.businessId)
        queryParams.append("businessId", params.businessId);
      if (params?.locationId)
        queryParams.append("locationId", params.locationId);
      if (params?.startDate) queryParams.append("startDate", params.startDate);
      if (params?.endDate) queryParams.append("endDate", params.endDate);
      if (params?.isActive !== undefined)
        queryParams.append("isActive", String(params.isActive));

      const queryString = queryParams.toString();
      return api.get<AvailabilityOverrideDto[]>(
        queryString ? `/overrides?${queryString}` : "/overrides"
      );
    },

    createOverride: async (
      data: CreateAvailabilityOverrideDto
    ): Promise<AvailabilityOverrideDto> => {
      return api.post<AvailabilityOverrideDto>("/overrides", data);
    },

    deleteOverride: async (
      overrideId: string
    ): Promise<{ message: string }> => {
      return api.delete<{ message: string }>(`/overrides/${overrideId}`);
    },

    getTimeSlots: async (params: {
      userId: string;
      businessId: string;
      locationId: string;
      startAt: string;
      endAt: string;
      excludeAppointmentId?: string;
    }): Promise<TimeSlot[]> => {
      const queryParams = new URLSearchParams();
      queryParams.append("userId", params.userId);
      queryParams.append("businessId", params.businessId);
      queryParams.append("locationId", params.locationId);
      queryParams.append("startAt", params.startAt);
      queryParams.append("endAt", params.endAt);
      if (params.excludeAppointmentId) queryParams.append("excludeAppointmentId", params.excludeAppointmentId);
      const queryString = queryParams.toString();
      return api.get<TimeSlot[]>(`/timeslots?${queryString}`);
    },
  };
}

const availabilityApiService = createAvailabilityApiService();
export { availabilityApiService };
